import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, 
  Plus, Clock, AlertCircle,
  ArrowRight, CheckCircle2, Loader2,
  XCircle
} from 'lucide-react';
import { stockAdjustmentService } from '../../../../services/erpApi';
import { Toast } from '../../../../components';
import { useToast } from '../../../../hooks/useToast';
import './StockAdjustmentDashboard.css';

interface AdjustmentRequest {
  id: number;
  request_number: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reason?: string;
  notes?: string;
  created_by?: number;
  created_by_name?: string;
  approved_by?: number;
  approved_by_name?: string;
  approved_at?: string;
  created_at: string;
  items_count?: number;
}

interface WorkflowStep {
  label: string;
  count: number;
  icon: any;
  color: string;
  bg: string;
}

const StockAdjustmentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [requests, setRequests] = useState<AdjustmentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Workflow: Pendentes - Aprovados - Rejeitados
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([
    { label: 'Pendentes', count: 0, icon: Clock, color: 'step-amber', bg: '' },
    { label: 'Aprovados', count: 0, icon: CheckCircle2, color: 'step-emerald', bg: '' },
    { label: 'Rejeitados', count: 0, icon: XCircle, color: 'step-rose', bg: '' },
  ]);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      // Buscar todos os status
      const [pendingResponse, approvedResponse, rejectedResponse] = await Promise.all([
        stockAdjustmentService.list({ status: 'pending', limit: 1000 }),
        stockAdjustmentService.list({ status: 'approved', limit: 1000 }),
        stockAdjustmentService.list({ status: 'rejected', limit: 1000 })
      ]);
      
      const allRequests: AdjustmentRequest[] = [
        ...(pendingResponse.success ? pendingResponse.data || [] : []),
        ...(approvedResponse.success ? approvedResponse.data || [] : []),
        ...(rejectedResponse.success ? rejectedResponse.data || [] : [])
      ];
      
      setRequests(allRequests);
      updateWorkflowCounts(allRequests);
    } catch (error: any) {
      console.error('Erro ao carregar pedidos de ajuste:', error);
      showToast('Erro ao carregar pedidos de ajuste', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const updateWorkflowCounts = (requests: AdjustmentRequest[]) => {
    setWorkflowSteps([
      { label: 'Pendentes', count: requests.filter(r => r.status === 'pending').length, icon: Clock, color: 'step-amber', bg: '' },
      { label: 'Aprovados', count: requests.filter(r => r.status === 'approved').length, icon: CheckCircle2, color: 'step-emerald', bg: '' },
      { label: 'Rejeitados', count: requests.filter(r => r.status === 'rejected').length, icon: XCircle, color: 'step-rose', bg: '' },
    ]);
  };

  const getStatusConfig = (request: AdjustmentRequest) => {
    switch (request.status) {
      case 'pending': return { label: 'Pendente', color: 'status-pending', icon: Clock };
      case 'approved': return { label: 'Aprovado', color: 'status-approved', icon: CheckCircle2 };
      case 'rejected': return { label: 'Rejeitado', color: 'status-rejected', icon: XCircle };
      case 'cancelled': return { label: 'Cancelado', color: 'status-cancelled', icon: XCircle };
      default: return { label: 'Desconhecido', color: 'status-default', icon: AlertCircle };
    }
  };

  const handleRowClick = (request: AdjustmentRequest) => {
    navigate(`/erp/stock/adjustments/${request.id}`);
  };

  const filteredRequests = requests.filter(request => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      request.request_number?.toLowerCase().includes(search) ||
      request.id.toString().includes(search) ||
      request.created_by_name?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Agora';
    if (hours < 24) return `Há ${hours}h`;
    if (days === 1) return 'Ontem';
    return `Há ${days}d`;
  };

  return (
    <div className="stock-adjustment-dashboard">
      {/* Header */}
      <div className="stock-adjustment-dashboard-header">
        <div>
          <h1 className="stock-adjustment-dashboard-title">
            Ajuste de <span className="stock-adjustment-dashboard-title-accent">Estoque</span>
          </h1>
          <p className="stock-adjustment-dashboard-subtitle">
            Gestão de Ajustes entre Estoque Físico e Digital
          </p>
        </div>
        <div className="stock-adjustment-dashboard-actions">
          <button className="stock-adjustment-dashboard-btn-secondary">
            <Filter size={16} /> Filtros
          </button>
          <button 
            className="stock-adjustment-dashboard-btn-primary"
            onClick={() => navigate('/erp/stock/adjustments/new')}
          >
            <Plus size={16} /> Novo Pedido
          </button>
        </div>
      </div>

      {/* Pipeline workflow bar */}
      <div className="stock-adjustment-dashboard-workflow">
        <div className="stock-adjustment-dashboard-workflow-header">
          <h3>
            <Clock size={16} />
            Status dos Pedidos
          </h3>
        </div>
        <div className="stock-adjustment-dashboard-workflow-grid">
          {workflowSteps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div 
                key={idx} 
                className={`stock-adjustment-dashboard-workflow-step ${step.color}`}
              >
                <div className="stock-adjustment-dashboard-workflow-icon">
                  <Icon size={24} />
                  {step.count > 0 && (
                    <div className="stock-adjustment-dashboard-workflow-badge">
                      {step.count}
                    </div>
                  )}
                </div>
                <p className="stock-adjustment-dashboard-workflow-label">{step.label}</p>
                {idx < workflowSteps.length - 1 && (
                  <div className="stock-adjustment-dashboard-workflow-connector" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Active Loads List */}
      <div className="stock-adjustment-dashboard-card">
        <div className="stock-adjustment-dashboard-card-header">
          <h3 className="stock-adjustment-dashboard-card-title">Pedidos de Ajuste</h3>
          <div className="stock-adjustment-dashboard-search-wrapper">
            <div className="stock-adjustment-dashboard-search">
              <Search className="stock-adjustment-dashboard-search-icon" size={14} />
              <input
                type="text"
                placeholder="Buscar por número ou criador..."
                className="stock-adjustment-dashboard-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="stock-adjustment-dashboard-loading">
            <Loader2 size={24} className="stock-adjustment-dashboard-spinner" />
            <p>Carregando...</p>
          </div>
        ) : (
          <div className="stock-adjustment-dashboard-table-wrapper">
            <table className="stock-adjustment-dashboard-table">
              <thead>
                <tr>
                  <th>Número do Pedido</th>
                  <th>Criado por</th>
                  <th className="text-center">Itens</th>
                  <th className="text-center">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="stock-adjustment-dashboard-empty">
                      <p>Nenhum pedido de ajuste encontrado</p>
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => {
                    const config = getStatusConfig(request);
                    const Icon = config.icon;
                    return (
                      <tr
                        key={request.id}
                        className="stock-adjustment-dashboard-table-row"
                        onClick={() => handleRowClick(request)}
                      >
                        <td>
                          <div className="stock-adjustment-dashboard-invoice-info">
                            <span className="stock-adjustment-dashboard-invoice-id">
                              {request.request_number}
                            </span>
                            <span className="stock-adjustment-dashboard-invoice-number">
                              <CheckCircle2 size={10} /> ID: {request.id}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="stock-adjustment-dashboard-supplier-name">
                            {request.created_by_name || `Usuário #${request.created_by}`}
                          </span>
                          <p className="stock-adjustment-dashboard-date">
                            <Clock size={10} /> {formatDate(request.created_at)}
                          </p>
                        </td>
                        <td className="text-center">
                          <span className="stock-adjustment-dashboard-items-badge">
                            {request.items_count || 0} Itens
                          </span>
                        </td>
                        <td>
                          <div className="stock-adjustment-dashboard-status-wrapper">
                            <span className={`stock-adjustment-dashboard-status ${config.color}`}>
                              <Icon size={12} />
                              {config.label}
                            </span>
                          </div>
                        </td>
                        <td className="text-right">
                          <button 
                            className="stock-adjustment-dashboard-btn-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(request);
                            }}
                          >
                            Ver <ArrowRight size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast Component */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default StockAdjustmentDashboard;
